import React, { useMemo, useRef, useEffect, useCallback } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import PropTypes from "prop-types";
import ActionNode from "./nodes/ActionNode.react.js";
import {
  getCanvasCenterScreenPoint,
  shouldTryInitialFit,
  nodesHaveDomSize,
  anyNodeInWrapperView,
  planPhaseViewportChange,
  isViewportSnapshot,
} from "../utils/canvasViewport.js";

/**
 * 画布（包 React Flow）。
 * isEdit=False 时禁用拖拽/连线/编辑，但仍可点击/选中/缩放。
 *
 * 拖拽添加节点走 **document 级监听**（不用 ReactFlow 内部 onDrop）：
 *   - React Flow v12 内部用 d3-drag 处理节点拖动，d3-drag 会在内部
 *     stopPropagation，导致外层 div 和 ReactFlow 自身都收不到 HTML5 drop。
 *   - 改用 document.addEventListener('drop', ...) + getBoundingClientRect()
 *     判断 drop 坐标是否在画布 wrapper 范围内：保证 100% 收到 drop。
 *   - 这是与 d3-drag 完全兼容的方案。
 *
 * 视口：
 *   - 点击添加：通过 onCanvasApi 暴露 getCanvasCenterFlowPos（当前 wrapper 中心 → flow 坐标）
 *   - 重载有节点：容器尺寸就绪后 fitView 一次（不依赖 ReactFlow 的 fitView prop）
 *   - 空画布起步：不自动 fit，避免拖入第一个节点时视口被拉开
 *   - 多 phase：按 phase 记忆视口；切相保存旧相相机，恢复新相缓存，首次进入有节点则 fit
 */
function FlowCanvas({
  nodes,
  edges,
  schema,
  isEdit,
  activePhase = "main",
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onDropAction,
  onCanvasApi,
  /** 重载已有图时为 true：容器就绪后对可见节点 fit 一次；空画布为 false */
  fitOnMount = false,
}) {
  const { screenToFlowPosition, fitView, getViewport, setViewport } =
    useReactFlow();
  const wrapperRef = useRef(null);
  const hasFitOnce = useRef(false);
  const startedWithNodes = useRef(fitOnMount);
  /** @type {React.MutableRefObject<Record<string, {x:number,y:number,zoom:number}>>} */
  const viewportByPhaseRef = useRef({});
  const phaseRef = useRef(activePhase);

  const getCanvasCenterFlowPos = useCallback(() => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    const screen = getCanvasCenterScreenPoint(rect);
    if (!screen) return null;
    try {
      return screenToFlowPosition(screen);
    } catch (e) {
      console.error("[TFE] getCanvasCenterFlowPos failed", e);
      return null;
    }
  }, [screenToFlowPosition]);

  // 把 API 交给上层（点击添加注入视口中心）
  useEffect(() => {
    if (!onCanvasApi) return undefined;
    onCanvasApi({ getCanvasCenterFlowPos });
    return () => onCanvasApi(null);
  }, [onCanvasApi, getCanvasCenterFlowPos]);

  /**
   * React Flow v12 的 fitView 是异步的（fitViewQueued + setNodes/updateNodeInternals）。
   * 旧逻辑在调用后立刻 hasFitOnce=true 并同步 getViewport，会在节点未测量时锁死且读到旧视口。
   * 必须：DOM 尺寸就绪 → await fitView → 校验节点进入可视区后才锁死。
   */
  const tryInitialFit = useCallback(async () => {
    const el = wrapperRef.current;
    const rect = el?.getBoundingClientRect();
    const width = rect?.width ?? 0;
    const height = rect?.height ?? 0;
    if (
      !shouldTryInitialFit({
        hasFitOnce: hasFitOnce.current,
        startedWithNodes: startedWithNodes.current,
        width,
        height,
        nodeCount: nodes.length,
      })
    ) {
      return;
    }
    // 自定义节点尚未量到宽高时不锁死，留给 ResizeObserver / effect 重试
    if (!nodesHaveDomSize(el)) return;

    try {
      await fitView({ maxZoom: 1, duration: 0 });
    } catch (e) {
      console.error("[TFE] initial fitView failed", e);
      return;
    }

    if (!anyNodeInWrapperView(el)) {
      // 异步 fit 空转（常见于测量被 dimensions filter 打乱时），不锁死以便重试
      return;
    }

    hasFitOnce.current = true;
    try {
      const vp = getViewport();
      if (isViewportSnapshot(vp)) {
        viewportByPhaseRef.current = {
          ...viewportByPhaseRef.current,
          [phaseRef.current]: { x: vp.x, y: vp.y, zoom: vp.zoom },
        };
      }
    } catch (e) {
      console.error("[TFE] getViewport after initial fit failed", e);
    }
  }, [fitView, getViewport, nodes.length]);

  // mount / 节点就绪后尝试；ResizeObserver 覆盖 Dash/flex 首帧尺寸为 0 的情况
  useEffect(() => {
    void tryInitialFit();
  }, [tryInitialFit]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      void tryInitialFit();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [tryInitialFit]);

  // 切 phase：保存离开相的视口，恢复进入相的缓存；首次进入有节点则 fitView
  useEffect(() => {
    const prevPhase = phaseRef.current;
    if (prevPhase === activePhase) return;

    let currentViewport = null;
    try {
      currentViewport = getViewport();
    } catch (e) {
      console.error("[TFE] getViewport failed on phase change", e);
    }

    const { savedByPhase, action } = planPhaseViewportChange({
      prevPhase,
      nextPhase: activePhase,
      currentViewport,
      savedByPhase: viewportByPhaseRef.current,
      nextNodeCount: nodes.length,
    });
    viewportByPhaseRef.current = savedByPhase;
    phaseRef.current = activePhase;

    if (action.type === "restore") {
      try {
        setViewport(action.viewport, { duration: 0 });
      } catch (e) {
        console.error("[TFE] setViewport failed on phase restore", e);
      }
      return;
    }

    if (action.type === "fit") {
      // 等可见节点提交到 RF 后再 fit，避免量到空图；await 确认异步 fit 生效
      requestAnimationFrame(() => {
        void (async () => {
          try {
            await fitView({ maxZoom: 1, duration: 0 });
            const vp = getViewport();
            if (isViewportSnapshot(vp)) {
              viewportByPhaseRef.current = {
                ...viewportByPhaseRef.current,
                [activePhase]: { x: vp.x, y: vp.y, zoom: vp.zoom },
              };
            }
          } catch (e) {
            console.error("[TFE] fitView failed on phase enter", e);
          }
        })();
      });
    }
  }, [activePhase, nodes.length, getViewport, setViewport, fitView]);

  const handleMoveEnd = useCallback(
    (_event, viewport) => {
      if (!isViewportSnapshot(viewport)) return;
      viewportByPhaseRef.current = {
        ...viewportByPhaseRef.current,
        [activePhase]: {
          x: viewport.x,
          y: viewport.y,
          zoom: viewport.zoom,
        },
      };
    },
    [activePhase]
  );

  /**
   * Document 级 drop：完全绕过 ReactFlow 内部。
   * 只在拖拽源携带 application/tfe-action 时才处理。
   */
  useEffect(() => {
    if (!isEdit) return undefined;
    const onDocDrop = (event) => {
      // 必须 preventDefault，否则浏览器会执行默认行为（导航到图片/链接）
      event.preventDefault();
      const actionKey =
        event.dataTransfer.getData("application/tfe-action") ||
        event.dataTransfer.getData("text/plain");
      if (!actionKey) return; // 不是从组件库拖出来的，忽略
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      // 用 contains 判断（不依赖 rect 数值正确；rect 在某些嵌入场景会为 0）
      if (!wrapper.contains(event.target)) return;
      try {
        const position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
        if (onDropAction) onDropAction(actionKey, position);
      } catch (e) {
        // 运行时错误（保留——用户应该看到）
        console.error("[TFE] screenToFlowPosition failed", e);
      }
    };
    // 关键：document 级 dragover 也必须 preventDefault，否则 drop 不触发
    const onDocDragOver = (event) => {
      if (event.dataTransfer && event.dataTransfer.types) {
        const types = Array.from(event.dataTransfer.types);
        if (types.includes("application/tfe-action") || types.includes("text/plain")) {
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
        }
      }
    };
    document.addEventListener("drop", onDocDrop);
    document.addEventListener("dragover", onDocDragOver);
    return () => {
      document.removeEventListener("drop", onDocDrop);
      document.removeEventListener("dragover", onDocDragOver);
    };
  }, [isEdit, screenToFlowPosition, onDropAction]);

  const nodeTypes = useMemo(
    () => ({
      actionNode: (n) => (
        <ActionNode
          {...n}
          data={{
            ...n.data,
            schema,
          }}
        />
      ),
    }),
    [schema]
  );

  const rfNodes = useMemo(
    () =>
      nodes.map((n) => ({
        id: n.id,
        type: "actionNode",
        position: n.position,
        data: n,
      })),
    [nodes]
  );

  const rfEdges = useMemo(
    () =>
      edges.map((e) => ({
        id: e.id ?? `${e.source}-${e.target}`,
        source: e.source,
        target: e.target,
        animated: e.animated,
        // 关键：受控模式下必须把 selected 透传回 ReactFlow，
        // 否则点击边后重建 rfEdges 会丢掉选中态，导致按 Backspace/Delete 删不掉。
        selected: e.selected,
        style: {
          stroke: e.selected ? "#4f46e5" : "#9ca3af",
          strokeWidth: e.selected ? 3 : 2,
        },
      })),
    [edges]
  );

  return (
    <div ref={wrapperRef} className="tfe-canvas-wrapper">
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        nodesDraggable={isEdit}
        nodesConnectable={isEdit}
        edgesUpdatable={isEdit}
        edgesFocusable={isEdit}
        // 关键：elementsSelectable 必须与 isEdit 绑定。
        // 否则 React Flow store 计算 isInteractive = nodesDraggable || nodesConnectable ||
        // elementsSelectable 永远为 true，<Controls> 的 Toggle Interactivity 图标
        // 永远显示解锁状态。查看模式下 elementsSelectable=false → isInteractive=false →
        // React Flow 自动渲染锁定图标。
        elementsSelectable={isEdit}
        deleteKeyCode={isEdit ? ["Backspace", "Delete"] : null}
        connectionRadius={40}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={(_, n) => onNodeClick(n.id)}
        onPaneClick={() => onNodeClick(null)}
        onMoveEnd={handleMoveEnd}
        onInit={() => {
          void tryInitialFit();
        }}
      >
        <Background color="#cbd5e1" gap={20} size={1} />
        {/* 保留缩放/fit-view/Toggle Interactivity 三个按钮。
            查看模式下图标自动锁定，且 elementsSelectable=false 让原生 onToggleInteractivity
            切换无效——受控 props 会立刻刷回 isEdit=false 的禁用配置。 */}
        <Controls />
      </ReactFlow>
    </div>
  );
}

FlowCanvas.propTypes = {
  nodes: PropTypes.array.isRequired,
  edges: PropTypes.array.isRequired,
  schema: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  /** 当前 phase value；用于按相记忆 / 恢复视口 */
  activePhase: PropTypes.string,
  onNodesChange: PropTypes.func.isRequired,
  onEdgesChange: PropTypes.func.isRequired,
  onConnect: PropTypes.func.isRequired,
  onNodeClick: PropTypes.func.isRequired,
  onDropAction: PropTypes.func,
  /** 画布 API 就绪回调：`({ getCanvasCenterFlowPos }) => void`；卸载时传 null */
  onCanvasApi: PropTypes.func,
  /** 是否在 mount 后对已有节点做一次 fitView（重载场景） */
  fitOnMount: PropTypes.bool,
};

export default FlowCanvas;
